import { useState, useCallback } from 'react';

export type BuiltinColumnId =
  | 'priority'
  | 'assignees'
  | 'due_date'
  | 'start_date'
  | 'time_estimated'
  | 'tags'
  | 'created_at';

// string covers 'cf_${uuid}' for custom field columns
export type ColumnId = BuiltinColumnId | string;

export interface ColumnDef {
  id: BuiltinColumnId;
  label: string;
  width: number; // px default
  defaultVisible: boolean;
}

export const ALL_COLUMNS: ColumnDef[] = [
  { id: 'priority',       label: 'Priorità',    width: 36,  defaultVisible: false },
  { id: 'assignees',      label: 'Assegnatari', width: 88,  defaultVisible: true  },
  { id: 'due_date',       label: 'Scadenza',    width: 110, defaultVisible: true  },
  { id: 'start_date',     label: 'Inizio',      width: 110, defaultVisible: false },
  { id: 'time_estimated', label: 'Stima',       width: 80,  defaultVisible: false },
  { id: 'tags',           label: 'Tag',         width: 140, defaultVisible: false },
  { id: 'created_at',     label: 'Creato',      width: 110, defaultVisible: false },
];

const DEFAULT_CF_WIDTH = 120;
const DEFAULT_NAME_WIDTH = 350;

const LS_KEY = 'zenda-list-columns-v1';
const LS_WIDTHS_KEY = 'zenda-list-col-widths-v1';

function getDefaultVisible(): Set<ColumnId> {
  return new Set(ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.id));
}

function loadFromStorage(): Set<ColumnId> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return getDefaultVisible();
    const arr = JSON.parse(raw) as ColumnId[];
    return new Set(arr);
  } catch {
    return getDefaultVisible();
  }
}

function loadWidths(): Record<string, number> {
  try {
    const raw = localStorage.getItem(LS_WIDTHS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, number>;
  } catch {
    return {};
  }
}

export function useListColumns() {
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnId>>(loadFromStorage);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(loadWidths);

  const toggleColumn = useCallback((id: ColumnId) => {
    setVisibleColumns(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      localStorage.setItem(LS_KEY, JSON.stringify([...next]));
      return next;
    });
  }, []);

  const isVisible = useCallback((id: ColumnId) => visibleColumns.has(id), [visibleColumns]);

  const getColumnWidth = useCallback((id: ColumnId): number => {
    if (columnWidths[id]) return columnWidths[id];
    if (id === 'task_name') return DEFAULT_NAME_WIDTH;
    const col = ALL_COLUMNS.find(c => c.id === id);
    return col ? col.width : DEFAULT_CF_WIDTH;
  }, [columnWidths]);

  const setColumnWidth = useCallback((id: ColumnId, width: number) => {
    const min = id === 'task_name' ? 200 : 40;
    const max = id === 'task_name' ? 800 : 500;
    const clamped = Math.max(min, Math.min(max, width));
    setColumnWidths(prev => {
      const next = { ...prev, [id]: clamped };
      localStorage.setItem(LS_WIDTHS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  return { visibleColumns, toggleColumn, isVisible, allColumns: ALL_COLUMNS, getColumnWidth, setColumnWidth };
}
